import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { newsAPI, classesAPI, eventsAPI, menuAPI, seedAPI, pagesAPI, enrollmentAPI, SITE_NAMES, SITE_COLORS } from '../../lib/api';
import { formatDate, formatShortDate, getDayName, isToday } from '../../lib/utils';
import { Button } from '../ui/button';
import { ArrowRight, Calendar, Users, UtensilsCrossed, ChevronRight, Star, Heart, BookOpen, Sparkles } from 'lucide-react';
import { SEO, buildOrganizationJsonLd } from '../seo/SEO';

export const HomePage = ({ siteKey }) => {
  const [news, setNews] = useState([]);
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [menu, setMenu] = useState([]);
  const [heroSettings, setHeroSettings] = useState({});
  const [enrollmentActive, setEnrollmentActive] = useState(false);
  const [loading, setLoading] = useState(true);

  const siteName = SITE_NAMES[siteKey];
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadData = async () => {
      try {
        await seedAPI.seedData().catch(() => {});
        
        const [newsRes, classesRes, eventsRes, menuRes, heroRes, enrollRes] = await Promise.all([
          newsAPI.getAll(siteKey),
          classesAPI.getAll(siteKey),
          eventsAPI.getAll(siteKey, true, true),
          menuAPI.getCurrentWeek(siteKey),
          pagesAPI.getPage(siteKey, 'homepage').catch(() => ({ data: {} })),
          enrollmentAPI.get(siteKey).catch(() => ({ data: {} }))
        ]);
        
        setNews(newsRes.data.slice(0, 3));
        setClasses(classesRes.data);
        setEvents(eventsRes.data.slice(0, 3));
        setMenu(menuRes.data);
        setHeroSettings(heroRes.data || {});
        setEnrollmentActive(enrollRes.data?.is_active || false);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [siteKey]);

  const features = siteKey === 'ms' ? [
    { icon: Heart, title: 'Individuální přístup', description: 'Každé dítě je jedinečné', color: '#FF6B6B' },
    { icon: Star, title: 'Hravá výuka', description: 'Učení skrze hru', color: '#FECA57' },
    { icon: Users, title: 'Skvělý kolektiv', description: 'Přátelské prostředí', color: '#26DE81' },
    { icon: BookOpen, title: 'Kvalitní vzdělávání', description: 'Zkušené učitelky', color: '#45AAF2' }
  ] : [
    { icon: Heart, title: 'Rodinná atmosféra', description: 'Jako doma', color: '#FF6B6B' },
    { icon: Sparkles, title: 'Malý kolektiv', description: 'Individuální péče', color: '#FF9F43' },
    { icon: Star, title: 'Laskavý přístup', description: 'S láskou k dětem', color: '#FECA57' },
    { icon: Users, title: 'Flexibilní péče', description: 'Dle vašich potřeb', color: '#A55EEA' }
  ];

  const todayMenu = menu.find(m => isToday(m.date));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <SEO
        siteKey={siteKey}
        title="Úvodní stránka"
        description={`${siteName} - ${siteKey === 'ms' ? 'Moderní mateřská škola v Nové Roli, zaměřená na všestranný rozvoj dětí ve věku 3-6 let.' : 'Rodinná dětská skupina v Nové Roli s laskavým a individuálním přístupem k malým dětem.'} Přihlaste své dítě k zápisu online.`}
        jsonLd={buildOrganizationJsonLd(siteKey)}
      />
      {/* Hero Section */}
      <section className="hero-section relative py-16 lg:py-24 overflow-hidden" style={{ background: `linear-gradient(135deg, #FFFDF7 0%, ${colors.primary}10 50%, ${colors.secondary}10 100%)` }}>
        <div className="hero-blob hero-blob-1" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }} />
        <div className="hero-blob hero-blob-2" style={{ background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})` }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              {enrollmentActive && (
                <Link 
                  to={`/${siteKey}/zapis`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white mb-6 shadow-lg hover:scale-105 transition-transform"
                  style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                  data-testid="hero-enrollment-badge"
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  {siteKey === 'ms' ? 'Zápis probíhá!' : 'Príjímáme deti!'}
                </Link>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D3748] leading-tight mb-6">
                Vítejte v<br />
                <span style={{ color: colors.primary }}>{siteName}</span>
                {siteKey === 'ms' ? '' : '!'}
              </h1>
              <p className="text-lg text-[#64748B] mb-8 max-w-lg leading-relaxed">
                {heroSettings.hero_subtitle || (siteKey === 'ms' 
                  ? 'Moderní materská škola zamerená na všestranný rozvoj detí ve veku 3-6 let. Vytvárí bezpecné a podnětné prostredí.'
                  : 'Rodinná detská skupina pro nejmenší. Individuální prístup v malém kolektivu s láskyplnou pécí.')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to={`/${siteKey}/kontakt`}>
                  <Button 
                    size="lg" 
                    className="rounded-full text-white px-8 shadow-lg hover:scale-105 transition-transform"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                    data-testid="hero-cta-contact"
                  >
                    Kontaktujte nás
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to={`/${siteKey}/o-nas`}>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full px-8 border-2 hover:scale-105 transition-transform"
                    style={{ borderColor: colors.primary, color: colors.primary }}
                    data-testid="hero-cta-about"
                  >
                    Více o nás
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-fade-in-up animate-delay-200">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white">
                <img 
                  src={heroSettings.hero_image_url || (siteKey === 'ms' 
                    ? "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=800&q=80"
                    : "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80"
                  )}
                  alt="Deti" 
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl animate-float border-2 border-[#F0F4F8]">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
                    style={{ background: `${colors.primary}20` }}
                  >
                    {heroSettings.floating_emoji || (siteKey === 'ms' ? '👶' : '🏡')}
                  </div>
                  <div>
                    <div className="font-bold text-[#2D3748]" data-testid="floating-title">
                      {heroSettings.floating_title || (siteKey === 'ms' ? '3 třídy' : 'Malý kolektiv')}
                    </div>
                    <div className="text-sm text-[#64748B]" data-testid="floating-subtitle">
                      {heroSettings.floating_subtitle || (siteKey === 'ms' ? 'Pro děti 3-6 let' : 'Max. 12 dětí')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.title} 
                  className="feature-card animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`feature-card-${index}`}
                >
                  <div 
                    className="feature-icon mb-4"
                    style={{ backgroundColor: `${feature.color}20` }}
                  >
                    <Icon size={32} style={{ color: feature.color }} />
                  </div>
                  <h3 className="font-bold text-lg text-[#2D3748] mb-2">{feature.title}</h3>
                  <p className="text-[#64748B]">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* News Section */}
      {news.length > 0 && (
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold text-[#2D3748] mb-2">Aktuality</h2>
                <p className="text-[#64748B]">Nejnovější zprávy</p>
              </div>
              <Link to={`/${siteKey}/aktuality`} className="hidden sm:flex items-center gap-2 font-bold hover:gap-3 transition-all" style={{ color: colors.primary }}>
                Všechny aktuality
                <ChevronRight size={18} />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item, index) => (
                <Link 
                  key={item.id} 
                  to={`/${siteKey}/aktuality/${item.id}`}
                  className="news-card animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`news-card-${index}`}
                >
                  {item.image_url && (
                    <div className="overflow-hidden">
                      <img src={item.image_url} alt={item.title} className="news-card-image" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="text-sm text-[#64748B] mb-2">{formatDate(item.created_at)}</div>
                    <h3 className="font-bold text-lg text-[#2D3748] mb-2 line-clamp-2">{item.title}</h3>
                    <p className="text-[#64748B] text-sm line-clamp-2">{item.excerpt || item.content}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Classes Preview */}
      {classes.length > 0 && (
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#2D3748] mb-2">
                {siteKey === 'ms' ? 'Naše třídy' : 'Naše skupiny'}
              </h2>
              <p className="text-[#64748B]">
                {siteKey === 'ms' ? 'Třídy rozdělené podle věku' : 'Rodinné prostředí pro vaše děti'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {classes.slice(0, 3).map((cls, index) => (
                <Link 
                  key={cls.id} 
                  to={`/${siteKey}/tridy/${cls.id}`}
                  className="class-card animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`class-card-${index}`}
                >
                  {cls.image_url && (
                    <div className="overflow-hidden">
                      <img src={cls.image_url} alt={cls.name} className="w-full h-48 object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <div 
                      className="inline-block px-4 py-1.5 rounded-full text-sm font-bold mb-3"
                      style={{ background: `${colors.primary}15`, color: colors.primary }}
                    >
                      {cls.age_group}
                    </div>
                    <h3 className="font-bold text-xl text-[#2D3748] mb-2">{cls.name}</h3>
                    <p className="text-[#64748B] text-sm mb-4 line-clamp-2">{cls.description}</p>
                    <div className="flex items-center justify-between text-sm text-[#64748B]">
                      <span>{cls.teacher_name}</span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {cls.capacity} dětí
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Today's Menu & Events */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Today's Menu */}
            <div className="rounded-3xl p-6 lg:p-8" style={{ background: `linear-gradient(135deg, ${colors.secondary}10, ${colors.primary}10)` }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl" style={{ background: '#FECA57' }}>
                    🍽️
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#2D3748]">Dnešní jídelníček</h3>
                    <p className="text-sm text-[#64748B]">{todayMenu ? getDayName(todayMenu.date) : 'Není k dispozici'}</p>
                  </div>
                </div>
                <Link to={`/${siteKey}/jidelnicek`} className="text-sm font-bold" style={{ color: colors.primary }}>
                  Celý týden
                </Link>
              </div>
              
              {todayMenu ? (
                <div className="space-y-3">
                  {todayMenu.breakfast && (
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-xs text-[#64748B] mb-1">🌅 Snídaně</div>
                      <div className="text-[#2D3748] font-medium">{todayMenu.breakfast}</div>
                    </div>
                  )}
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-xs text-[#64748B] mb-1">🍲 Oběd</div>
                    <div className="text-[#2D3748] font-medium">{todayMenu.lunch}</div>
                  </div>
                  {todayMenu.snack_pm && (
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="text-xs text-[#64748B] mb-1">🍪 Svačina</div>
                      <div className="text-[#2D3748] font-medium">{todayMenu.snack_pm}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-[#64748B]">
                  Jídelníček není k dispozici
                </div>
              )}
            </div>

            {/* Upcoming Events */}
            <div className="rounded-3xl p-6 lg:p-8" style={{ background: `linear-gradient(135deg, ${colors.primary}10, ${colors.secondary}10)` }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl" style={{ background: colors.primary }}>
                    📅
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#2D3748]">Nadcházející akce</h3>
                    <p className="text-sm text-[#64748B]">Co nás čeká</p>
                  </div>
                </div>
                <Link to={`/${siteKey}/kalendar`} className="text-sm font-bold" style={{ color: colors.primary }}>
                  Vše
                </Link>
              </div>
              
              {events.length > 0 ? (
                <div className="space-y-3">
                  {events.map((event) => (
                    <div key={event.id} className="event-card bg-white">
                      <div className="event-date-badge" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}>
                        <span className="text-xl font-bold">{new Date(event.start_date).getDate()}</span>
                        <span className="text-xs">{formatShortDate(event.start_date).split(' ')[1]}</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-[#2D3748]">{event.title}</h4>
                        {event.location && (
                          <p className="text-sm text-[#64748B]">{event.location}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#64748B]">
                  Žádné nadcházející akce
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-3xl p-8 lg:p-12 text-center text-white"
            style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Máte zájem o {siteKey === 'ms' ? 'naši školku' : 'naši skupinu'}?
            </h2>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto text-lg">
              Rádi vám zodpovíme všechny dotazy. Neváhejte nás kontaktovat!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to={`/${siteKey}/kontakt`}>
                <Button size="lg" className="rounded-full bg-white hover:bg-white/90 px-8 font-bold" style={{ color: colors.primary }}>
                  Napište nám
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
